import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core'
import { Validators, FormControl } from '@angular/forms'
import { Subscription, combineLatest, map } from 'rxjs'
import { GlobalConstants } from 'src/app/constants/global-constants'
import {
  CompanyEntityAPIModel,
  CompanyGroupAPIModel,
  RelatedCompaniesAPIModel,
  SelectedCompanyAPIModel
} from 'src/app/models/api.model'
import { AuthService } from 'src/app/services/auth/auth.service'
import { CommonService } from 'src/app/services/common/common.service'
import { DynamicComponentLoaderService } from 'src/app/services/dynamic-component-loader/dynamic-component-loader.service'
import { InitializeService } from 'src/app/services/initialize/initialize.service'

export interface CompanyOption {
  key: string
  value: string
  disabled: boolean
  selected: boolean
  type: string
  groupId?: string
}

@Component({
  selector: 'app-client-selector',
  templateUrl: './client-selector.component.html',
  styleUrls: ['./client-selector.component.scss']
})
export class ClientSelectorComponent implements OnInit {
  @ViewChild('selectpg-popup', { static: true }) selectPgPopup: ElementRef
  @ViewChild('dropdown', { static: true }) dropdown: ElementRef

  selectpgPopup: any

  clientFormControl: FormControl = new FormControl('', [Validators.required])

  relatedCompaniesOptions: CompanyOption[] = []
  relatedCompanies: RelatedCompaniesAPIModel

  groupedOptions: { group: CompanyOption; entities: CompanyOption[]; expanded: boolean }[] = []
  ungroupedEntities: CompanyOption[] = []

  showDropdown = false
  searchText = ''

  combined$: Subscription

  companyType = GlobalConstants.COMPANY_TYPE
  previouslySelectedCompanies: SelectedCompanyAPIModel[] = []

  constructor(
    private cs: CommonService,
    private initializeService: InitializeService,
    private elementRef: ElementRef,
    private authService: AuthService,
    private dynamicComponentLoaderService: DynamicComponentLoaderService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.selectpgPopup = this.elementRef.nativeElement.querySelector('#selectpg-popup')
    this.selectpgPopup.style.display = 'block'
    const relatedCompaniesSubscription$ = this.initializeService.getRelatedCompanies()
    const selectedCompaniesSubscription$ = this.initializeService.getSelectedCompanies()
    const result$ = combineLatest({
      relatedCompanies: relatedCompaniesSubscription$,
      selectedCompanies: selectedCompaniesSubscription$
    }).pipe(
      map((data: { relatedCompanies: RelatedCompaniesAPIModel; selectedCompanies: SelectedCompanyAPIModel[] }) => {
        return { relatedCompanies: data.relatedCompanies, selectedCompanies: data.selectedCompanies }
      })
    )

    this.combined$ = result$.subscribe(({ relatedCompanies, selectedCompanies }) => {
      this.findRelatedCompanies(relatedCompanies)

      this.buildGroups()

      this.findSelectedCompanies(selectedCompanies)
      this.previouslySelectedCompanies = selectedCompanies
    })

    this.renderer.listen('window', 'click', (event: Event) => {
      if (!this.dropdown.nativeElement.contains(event.target)) {
        this.showDropdown = false
      }
    })
  }

  submit() {
    if (!this.relatedCompaniesOptions.find(rco => rco.selected)) {
      return this.cs.showError('Please select client')
    }
    this.combined$?.unsubscribe()
    const selectedGroup = this.relatedCompaniesOptions.filter(
      rco => rco.selected && rco.type === GlobalConstants.COMPANY_TYPE.GROUP
    )?.[0]
    const selectedGroupId = selectedGroup?.key || ''
    const selectedEnities = this.relatedCompaniesOptions.filter(
      rco => rco.selected && rco.type === GlobalConstants.COMPANY_TYPE.ENTITY
    )
    const selectedEnityIds = selectedEnities?.map(rco => rco.key)
    this.authService.dispatchSelectClientAction(selectedGroupId, selectedEnityIds, this.success())
  }

  success() {
    const dynamicComponentLoaderService = this.dynamicComponentLoaderService
    const commonService = this.cs
    const reloadPage = this.reloadPage

    return function perform() {
      dynamicComponentLoaderService.closeComponent(GlobalConstants.DYNAMIC_MODEL.CLIENT_SELECTOR.key)
      reloadPage(commonService)
    }
  }

  reloadPage(commonService: CommonService) {
    setTimeout(() => {
      window.location.href = `/${GlobalConstants.ROUTING.DASHBOARD}`
    }, 100)
  }

  close() {
    if (this.previouslySelectedCompanies.length == 0) {
      return this.cs.showError('Please select client')
    }
    this.dynamicComponentLoaderService.closeComponent(GlobalConstants.DYNAMIC_MODEL.CLIENT_SELECTOR.key)
  }

  private findRelatedCompanies(relatedCompanies: RelatedCompaniesAPIModel) {
    if (!relatedCompanies) return

    this.relatedCompaniesOptions = []

    this.addEntitiesToOptions(relatedCompanies.entity)
    this.addGroupsToOptions(relatedCompanies.group)

    this.relatedCompanies = relatedCompanies
  }

  private addEntitiesToOptions(entities?: CompanyEntityAPIModel[]): void {
    if (!entities) return

    for (const entity of entities) {
      this.addCompanyOption(entity.id, entity.name, GlobalConstants.COMPANY_TYPE.ENTITY)
    }
  }

  private addGroupsToOptions(groups?: CompanyGroupAPIModel[]): void {
    if (!groups) return

    for (const group of groups) {
      this.addCompanyOption(group.id, group.name, GlobalConstants.COMPANY_TYPE.GROUP)

      for (const entity of group.relatedEntities) {
        this.addCompanyOption(entity.id, entity.name, GlobalConstants.COMPANY_TYPE.ENTITY, group.id)
      }
    }
  }

  private addCompanyOption(key: string, value: string, type: string, groupId?: string): void {
    this.relatedCompaniesOptions.push({
      key,
      value,
      disabled: false,
      selected: false,
      type,
      groupId
    })
  }

  private findSelectedCompanies(selectedCompanies: SelectedCompanyAPIModel[]) {
    if (!selectedCompanies || selectedCompanies.length === 0) return

    const group = selectedCompanies.find(sc => sc.type === GlobalConstants.COMPANY_TYPE.GROUP)

    if (group) {
      const groupOption = this.relatedCompaniesOptions.find(
        rco => rco.key === group.id && rco.type === GlobalConstants.COMPANY_TYPE.GROUP
      )

      if (groupOption) {
        this.toggleOption(groupOption)
      }
    } else {
      this.relatedCompaniesOptions.forEach(option => {
        const selected = selectedCompanies.find(sc => sc.id === option.key && sc.type === option.type)

        if (selected) {
          option.selected = true
        }
      })
    }
  }

  private buildGroups() {
    const map = new Map<string, { group: CompanyOption; entities: CompanyOption[]; expanded: boolean }>()

    this.ungroupedEntities = []

    this.relatedCompaniesOptions.forEach(option => {
      if (option.type === GlobalConstants.COMPANY_TYPE.GROUP) {
        map.set(option.key, {
          group: option,
          entities: [],
          expanded: false
        })
      } else if (option.groupId) {
        if (!map.has(option.groupId)) {
          const group = this.relatedCompaniesOptions.find(
            o => o.key === option.groupId && o.type === GlobalConstants.COMPANY_TYPE.GROUP
          )

          map.set(option.groupId, {
            group,
            entities: [],
            expanded: false
          })
        }

        map.get(option.groupId).entities.push(option)
      } else {
        this.ungroupedEntities.push(option)
      }
    })

    this.groupedOptions = Array.from(map.values())
  }

  toggleGroup(group: any, event: Event) {
    event.stopPropagation()
    group.expanded = !group.expanded
  }

  searchCompanies() {
    const search = this.searchText?.toLowerCase()?.trim()

    if (!search) {
      this.buildGroups()
      return
    }

    this.ungroupedEntities = []

    const map = new Map<string, { group: CompanyOption; entities: CompanyOption[]; expanded: boolean }>()

    this.relatedCompaniesOptions.forEach(option => {
      const optionMatch = option.value.toLowerCase().includes(search)

      if (option.type === GlobalConstants.COMPANY_TYPE.GROUP) {
        if (optionMatch) {
          map.set(option.key, {
            group: option,
            entities: [],
            expanded: true
          })
        }
      } else if (option.groupId) {
        const group = this.relatedCompaniesOptions.find(
          g => g.key === option.groupId && g.type === GlobalConstants.COMPANY_TYPE.GROUP
        )

        const groupMatch = group?.value?.toLowerCase()?.includes(search)

        if (optionMatch || groupMatch) {
          if (!map.has(option.groupId)) {
            map.set(option.groupId, {
              group,
              entities: [],
              expanded: true
            })
          }

          map.get(option.groupId).entities.push(option)
        }
      } else {
        if (optionMatch) {
          this.ungroupedEntities.push(option)
        }
      }
    })

    this.groupedOptions = Array.from(map.values())
  }

  toggleOption(company: CompanyOption): void {
    if (company.disabled) return

    company.selected = !company.selected

    if (company.type === GlobalConstants.COMPANY_TYPE.GROUP) {
      company.selected ? this.selectGroup(company) : this.deselectGroup(company.key)
    }
  }

  get hasSelection(): boolean {
    return this.relatedCompaniesOptions.some(rco => rco.selected)
  }

  clearSelection(): void {
    this.relatedCompaniesOptions.forEach(rco => {
      rco.selected = false
      rco.disabled = false
    })
  }

  private selectGroup(company: CompanyOption): void {
    const selectedCompanyIds = this.getRelatedEntities(company)
    selectedCompanyIds.push(company.key)

    this.relatedCompaniesOptions.forEach(relatedCompany => {
      const isSelected = selectedCompanyIds.includes(relatedCompany.key)
      let isDisabled = !isSelected
      if (relatedCompany.type === GlobalConstants.COMPANY_TYPE.ENTITY && isSelected) {
        isDisabled = !isDisabled
      }
      relatedCompany.selected = isSelected
      relatedCompany.disabled = isDisabled
    })
  }

  private deselectGroup(groupKey: string): void {
    this.relatedCompaniesOptions.forEach(relatedCompany => {
      relatedCompany.disabled = false
      if (relatedCompany.groupId === groupKey) {
        relatedCompany.selected = false
      }
    })
  }

  private getRelatedEntities(company: CompanyOption): string[] {
    const group = this.relatedCompanies.group.find(relatedCompany => relatedCompany.id === company.key)
    return group ? group.relatedEntities.map(entity => entity.id) : []
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown
  }

  get selectedOptionsCount() {
    return (
      this.relatedCompaniesOptions?.filter(rco => rco?.selected && rco?.type === GlobalConstants.COMPANY_TYPE.ENTITY)
        ?.length || 0
    )
  }

  ngOnDestroy() {
    this.combined$?.unsubscribe()
  }
}
